// UTXO-Wallet-Import via Blockchair Address/xpub Dashboard.
//
// User wählt eine Chain (BTC/LTC/DOGE/BCH) und fügt eine Adresse ODER einen
// xpub/zpub ein. Die App ruft /api/blockchain-lookup?mode=wallet auf und übergibt
// die geparsten Ein-/Auszahlungen an den Parent via onImport — identischer Flow
// wie CSV/Solana. xpub deckt das GANZE HD-Wallet ab (alle abgeleiteten Adressen).
//
// Privacy: Adresse/xpub geht nur über unser Backend an Blockchair (Sub-Processor),
// wird nicht persistiert. Reiner Lesezugriff.

import { useState, useEffect } from 'react';
import { F, R } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import InfoBanner from './InfoBanner';
import ExchangeLogo from './ExchangeLogo';
import ChainLogo from './ChainLogo';
import Tap from './Tap';
import PickerSheet from './PickerSheet';
import ImportReviewScreen from '../screens/ImportReviewScreen';
import { authHeader } from '../services/authHeader';
import { listWallets, ensureOwnWallets } from '../services/walletRegistry';
import { HW_WALLETS, SW_WALLETS } from '../utils/exchanges';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// Unterstützte Chains (Symbol → CoinGecko-ID für den Preis-Backfill).
// Exportiert, damit die Wallet-Registry (Profil) denselben Chain-Satz nutzt.
export const CHAINS = [
  { coin: 'BTC',  name: 'Bitcoin',      coinId: 'bitcoin',      color: '#F7931A' },
  { coin: 'LTC',  name: 'Litecoin',     coinId: 'litecoin',     color: '#A6A9AA' },
  { coin: 'DOGE', name: 'Dogecoin',     coinId: 'dogecoin',     color: '#C2A633' },
  { coin: 'BCH',  name: 'Bitcoin Cash', coinId: 'bitcoin-cash', color: '#0AC18E' },
  { coin: 'SOL',  name: 'Solana',       coinId: 'solana',       color: '#9945FF' },
  { coin: 'XLM',  name: 'Stellar',      coinId: 'stellar',      color: '#3E4AE8' },
  { coin: 'XRP',  name: 'XRP',          coinId: 'ripple',       color: '#23292F' },
  { coin: 'ADA',  name: 'Cardano',      coinId: 'cardano',      color: '#0033AD' },
  // EVM-Netze (eine 0x-Adresse, der User wählt das Netz)
  { coin: 'ETH',     name: 'Ethereum', coinId: 'ethereum',     color: '#627EEA', evm: true },
  { coin: 'BASE',    name: 'Base',     coinId: 'ethereum',     color: '#0052FF', evm: true },
  { coin: 'POLYGON', name: 'Polygon',  coinId: 'matic-network', color: '#8247E5', evm: true },
  { coin: 'ARB',     name: 'Arbitrum', coinId: 'ethereum',     color: '#28A0F0', evm: true },
  { coin: 'OP',      name: 'Optimism', coinId: 'ethereum',     color: '#FF0420', evm: true },
  { coin: 'BNB',     name: 'BNB Chain', coinId: 'binancecoin', color: '#F3BA2F', evm: true },
];
// Chains ohne xpub-Konzept (Account-basiert / EVM): nur Einzeladresse (bzw. Stake-Adresse).
const ADDRESS_ONLY = new Set(['SOL', 'XLM', 'XRP', 'ADA', 'ETH', 'BASE', 'POLYGON', 'ARB', 'OP', 'BNB']);
const EVM_COINS = new Set(['ETH', 'BASE', 'POLYGON', 'ARB', 'OP', 'BNB']);

// Adresse ODER xpub: URL-sicherer Zeichensatz, plausible Länge. Genaue Prüfung
// macht Blockchair (404 bei ungültig) — wir verhindern hier nur Injection.
const VALUE_RE = /^[a-zA-Z0-9:_-]{20,140}$/;

export default function ChainWalletConnect({ onBack, onImport, txs = [], uid, showToast }) {
  const { T } = useTheme();
  const { t } = useT();
  const [coin, setCoin] = useState('BTC');
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  // Import läuft über die echte Review-Maske (ImportReviewScreen) wie beim CSV-Import:
  // erst holen + Preise inline laden → reviewResult, dann zeigt der Review-Screen jede
  // Zeile einzeln (abwählen/Konflikt/Stale-Replace), erst dort wird committet.
  const [reviewResult, setReviewResult] = useState(null); // { transactions, exchange, skippedTokens }
  const [priceProgress, setPriceProgress] = useState(null);
  const [savedWallets, setSavedWallets] = useState([]); // hinterlegte „Meine Wallets"
  const [pickerOpen, setPickerOpen] = useState(false);
  const [chainPickerOpen, setChainPickerOpen] = useState(false);

  // Hinterlegte Wallets laden — für Ein-Klick-Vorauswahl (Adresse + Label automatisch).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await listWallets(uid);
        if (alive) setSavedWallets(list.filter(w => CHAINS.some(c => c.coin === w.chain)));
      } catch { /* leer */ }
    })();
    return () => { alive = false; };
  }, [uid]);

  // Gespeicherte Wallet gewählt → Chain + Adresse + Name vorbefüllen.
  const pickWallet = (w) => {
    setCoin(w.chain);
    setValue(w.address || '');
    setLabel(w.label || '');
    setError(null);
    setInfo(null);
    setPickerOpen(false);
  };
  // Nur eine Marke als Name gewählt (nicht gespeichert) → nur Label setzen.
  const pickBrand = (name) => {
    setLabel(name);
    setPickerOpen(false);
  };

  const valueOk = VALUE_RE.test(value.trim());
  const activeChain = CHAINS.find(c => c.coin === coin) || CHAINS[0];
  const addressOnly = ADDRESS_ONLY.has(coin);

  // Holen → Preise inline laden → ab in die echte Review-Maske (ImportReviewScreen).
  // Dort sieht der User jede Zeile, kann abwählen, Konflikte/Dopplungen werden markiert,
  // und erst dort wird committet. Preise laden wir VORHER, damit die Maske EUR-Werte zeigt.
  const handleFetch = async () => {
    setError(null);
    setInfo(null);
    setReviewResult(null);
    if (!valueOk) { setError(t('cwc_value_invalid')); return; }
    setBusy(true);
    // Wallet-Label: NIE auf den Coin-/Netz-Ticker zurückfallen (sonst „MoonPay → ETH").
    // Leer → generisch „Wallet"; der User kann z.B. „Ledger" eintragen → „MoonPay → Ledger".
    const walletLabel = label.trim() || 'Wallet';
    try {
      const auth = await authHeader();
      let data;
      if (coin === 'SOL') {
        // Solana läuft über Helius (Enhanced Transactions: Swaps/SPL/Symbol-Auflösung),
        // NICHT über die EVM/Blockchair-Route — Alchemy kann Solana nicht so parsen.
        const res = await fetch(`${API_BASE}/api/solana-wallet-sync`, {
          method: 'POST',
          headers: { ...auth, 'content-type': 'application/json' },
          body: JSON.stringify({ address: value.trim(), label: walletLabel }),
        });
        data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || t('con_error_status', { status: res.status }));
      } else {
        const params = new URLSearchParams({ mode: 'wallet', coin, address: value.trim(), label: walletLabel });
        const res = await fetch(`${API_BASE}/api/blockchain-lookup?${params.toString()}`, { headers: auth });
        data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || t('con_error_status', { status: res.status }));
      }
      const imported = data.transactions || [];
      if (imported.length === 0) {
        setInfo(t('cwc_no_txs', { addr: value.trim().slice(0, 10) }));
        setBusy(false);
        return;
      }

      // Die eingegebene Adresse/xpub IST die eigene Wallet des Kunden → in „Meine
      // Wallets" registrieren (idempotent), damit die adressbasierte Eigen-Transfer-
      // Erkennung sie kennt. Gilt für JEDE direkte Wallet-Quelle (Solana + UTXO),
      // nicht nur Ledger. Fehlertolerant — darf den Import nie blockieren.
      try {
        if (uid && value.trim()) {
          await ensureOwnWallets(uid, [{ label: walletLabel, chain: coin, address: value.trim() }]);
        }
      } catch { /* Registrierung nicht blockierend */ }

      // Preise inline: historischer Tagespreis je (coinId, Datum), einmal pro Paar.
      const keys = new Set();
      const jobs = [];
      for (const tx of imported) {
        if (tx.coinId && tx.datum && (!tx.preis || tx.preis === 0) && tx.menge > 0) {
          const k = `${tx.coinId}|${tx.datum}`;
          if (!keys.has(k)) { keys.add(k); jobs.push({ coinId: tx.coinId, sym: tx.coin, datum: tx.datum }); }
        }
      }
      const priceMap = {};
      if (jobs.length > 0) {
        setPriceProgress({ done: 0, total: jobs.length });
        for (let i = 0; i < jobs.length; i++) {
          const { coinId, sym, datum } = jobs[i];
          try {
            const symQ = sym ? `&sym=${encodeURIComponent(sym)}` : '';
            const r = await fetch(
              `${API_BASE}/api/coin-price-history?coin=${encodeURIComponent(coinId)}&date=${encodeURIComponent(datum)}&currency=eur${symQ}&cv=2`,
              { headers: auth },
            );
            if (r.ok) {
              const d = await r.json();
              if (typeof d.price === 'number') priceMap[`${coinId}|${datum}`] = d.price;
            }
          } catch { /* weiter */ }
          setPriceProgress({ done: i + 1, total: jobs.length });
          if (i < jobs.length - 1) await new Promise(r => setTimeout(r, 80));
        }
        setPriceProgress(null);
      }

      // In die kanonische Zeilenform für ImportReviewScreen bringen: Preis gesetzt,
      // `hash` aus `txHash` (Chain-Hash → Konflikt-/Dedup-Erkennung im Review-Screen).
      const rows = imported.map(tx => {
        const p = priceMap[`${tx.coinId}|${tx.datum}`];
        const preis = (p != null && tx.menge > 0) ? p * tx.menge : (tx.preis || 0);
        return {
          ...tx,
          preis,
          stueck: tx.menge > 0 ? preis / tx.menge : 0,
          hash: tx.hash || tx.txHash || '',
          // On-Chain-Rows tragen ihren Hash als `hash` (Helius/Blockchair). Ohne
          // `txHash` erkennt handleImportCsv sie NICHT als On-Chain-Import →
          // blockchainVerified fehlt → enrichTransactions laeuft auf die (dem
          // Lookup unbekannten) Solana-Signaturen, jede Anfrage laeuft in den 7s-
          // Timeout → der Import haengt minutenlang „Blockchain wird geprueft".
          // txHash spiegeln → blockchainVerified → enrich uebersprungen.
          txHash: tx.txHash || tx.hash || '',
        };
      });

      const skipped = Array.isArray(data.skippedTokens) ? data.skippedTokens : [];
      if (skipped.length > 0 && showToast) {
        const names = skipped.slice(0, 6).map(s => s.sym).join(', ') + (skipped.length > 6 ? '…' : '');
        showToast(t('cwc_skipped', { count: skipped.length, names }));
      }

      // deterministic: On-Chain-Daten, keine KI-Schätzung → Review-Screen blendet
      // die KI-Konfidenz-Anzeige aus und wertet alle Zeilen als gültig.
      setReviewResult({ transactions: rows, exchange: walletLabel, skippedTokens: skipped, deterministic: true, reconciliation: data.reconciliation || null });
    } catch (err) {
      setError(err.message || t('cwc_failed'));
    } finally {
      setBusy(false);
    }
  };

  // Review-Maske aktiv: dieselbe Oberfläche wie beim CSV-Import (Einzelzeilen,
  // abwählen, Konflikt-/Stale-Replace-Erkennung). onImport committet erst hier.
  if (reviewResult) {
    return (
      <ImportReviewScreen
        result={reviewResult}
        fileName={reviewResult.exchange}
        uid={uid}
        existingTxs={txs}
        onImport={async (cleanTxs, opts) => {
          await onImport(cleanTxs, opts);
          setReviewResult(null);
        }}
        onCancel={() => setReviewResult(null)}
      />
    );
  }

  const inputBase = {
    width: '100%', padding: '13px 15px', borderRadius: R.input,
    border: `1px solid ${T.line || T.border}`, background: T.surface, color: T.text,
    fontFamily: F.family, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '16px', maxWidth: 560, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        {/* Native App = Ionic-Route mit Swipe-back → Pfeil entfällt; Web behält ihn. */}
        {!(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) && (
          <button onClick={onBack} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: T.text,
            WebkitTapHighlightColor: 'transparent',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
        )}
        <div style={{ fontFamily: F.display, fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em', color: T.text }}>
          {t('cwc_title')}
        </div>
      </div>

      <InfoBanner tone="info" title={t('con_how_it_works')}>
        {t('cwc_how_body')}
      </InfoBanner>

      {/* Wallet wählen — Popup-Picker (gespeicherte Wallets ODER Marke). Ersetzt die
          lange Inline-Liste + das Freitext-Namensfeld. */}
      <div style={{ marginTop: 18 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.sub, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>
          {t('cwc_wallet_name')}
        </label>
        <Tap onClick={() => setPickerOpen(true)} scale={0.99} hoverScale={1.01} style={{
          width: '100%', padding: '13px 15px', borderRadius: R.input, textAlign: 'left',
          border: `1px solid ${T.line || T.border}`, background: T.surface, color: T.text,
          fontFamily: F.family, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          {label.trim() ? (
            <>
              <ExchangeLogo name={label} size={28} />
              <span style={{ fontSize: 14, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            </>
          ) : (
            <span style={{ fontSize: 14, color: T.sub, flex: 1 }}>{t('cwc_wallet_select')}</span>
          )}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
        </Tap>
        <div style={{ fontSize: 11, color: T.sub, marginTop: 4, fontFamily: F.family, lineHeight: 1.5 }}>
          {t('cwc_wallet_name_hint')}
        </div>
      </div>

      {/* Chain-Auswahl — Popup-Sheet (gleiche Optik wie der Coin-Picker) */}
      <div style={{ marginTop: 18 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.sub, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>
          {t('cwc_chain')}
        </label>
        <Tap onClick={() => setChainPickerOpen(true)} scale={0.99} hoverScale={1.01} style={{
          width: '100%', padding: '13px 15px', borderRadius: R.input, textAlign: 'left',
          border: `1px solid ${T.line || T.border}`, background: T.surface, color: T.text,
          fontFamily: F.family, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <ChainLogo coin={coin} size={28} color={activeChain.color} />
          <span style={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{activeChain.name}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
        </Tap>
      </div>

      {/* Adresse / xpub */}
      <div style={{ marginTop: 16 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.sub, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>
          {addressOnly ? t('cwc_value_label_addr') : t('cwc_value_label')}
        </label>
        <input
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={EVM_COINS.has(coin) ? '0x…' : addressOnly ? t('cwc_value_placeholder_addr') : t('cwc_value_placeholder')}
          autoCapitalize="off" autoCorrect="off" spellCheck={false}
          style={{ ...inputBase, fontFamily: F.mono, fontSize: 12, borderColor: value && !valueOk ? T.red : (T.line || T.border) }}
        />
        <div style={{ fontSize: 11, color: T.sub, marginTop: 4, fontFamily: F.family, lineHeight: 1.5 }}>
          {coin === 'ADA' ? t('cwc_value_hint_ada') : EVM_COINS.has(coin) ? t('cwc_value_hint_evm') : addressOnly ? t('cwc_value_hint_addr') : t('cwc_xpub_hint')}
        </div>
      </div>

      {error && <div style={{ marginTop: 14 }}><InfoBanner tone="error" title={t('con_error')}>{error}</InfoBanner></div>}
      {info && !error && <div style={{ marginTop: 14 }}><InfoBanner tone="info" title={t('con_status')}>{info}</InfoBanner></div>}

      <button
        onClick={handleFetch}
        disabled={!valueOk || busy}
        style={{
          marginTop: 22, width: '100%', padding: '14px',
          background: valueOk && !busy ? T.redGradient : T.line, color: T.white, border: 'none', borderRadius: R.md,
          fontFamily: F.family, fontSize: 15, fontWeight: 700,
          cursor: valueOk && !busy ? 'pointer' : 'not-allowed', opacity: busy ? 0.7 : 1,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {busy
          ? (priceProgress
              ? t('cwc_preview_pricing', { done: priceProgress.done, total: priceProgress.total })
              : t('cwc_loading', { name: activeChain.name }))
          : t('cwc_preview_cta')}
      </button>

      <div style={{ marginTop: 16, fontSize: 11, color: T.sub, lineHeight: 1.5, fontFamily: F.family }}>
        {t('cwc_readonly_note')}
      </div>

      {/* Wallet-Auswahl: gespeicherte Wallet (füllt Chain+Adresse+Name) ODER Marke (nur Name) */}
      <PickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t('cwc_pick_title')}
        searchPlaceholder={t('cwc_pick_search')}
        onCustom={pickBrand}
        customPrefix={t('cwc_pick_use')}
        sections={[
          // Eigene Wallets klar abgesetzt (Sektion + leichter Tint) — direkt
          // wählbar, füllt Chain + Adresse + Name automatisch.
          ...(savedWallets.length ? [{
            title: t('cwc_my_wallets'),
            items: savedWallets.map(w => ({
              key: 'sv-' + w.id,
              label: w.label,
              sub: (CHAINS.find(c => c.coin === w.chain) || {}).name || w.chain,
              logo: <ExchangeLogo name={w.label} size={30} />,
              accent: '#74A8DB',
              onPick: () => pickWallet(w),
            })),
          }] : []),
          // Neue Wallet: Marke wählen → danach Chain + Adresse eingeben.
          {
            title: t('cwc_new_wallet'),
            items: [...HW_WALLETS, ...SW_WALLETS].map(b => ({
              key: 'br-' + b.id,
              label: b.label,
              sub: t('cwc_pick_brand'),
              logo: <ExchangeLogo name={b.label} size={30} />,
              onPick: () => pickBrand(b.label),
            })),
          },
        ]}
      />

      {/* Chain-Auswahl im gleichen Sheet-Design */}
      <PickerSheet
        open={chainPickerOpen}
        onClose={() => setChainPickerOpen(false)}
        title={t('cwc_chain')}
        searchPlaceholder={t('cwc_pick_search')}
        items={CHAINS.map(c => ({
          key: c.coin,
          label: c.name,
          sub: c.coin,
          current: coin === c.coin,
          logo: <ChainLogo coin={c.coin} size={30} color={c.color} />,
          onPick: () => setCoin(c.coin),
        }))}
      />
    </div>
  );
}

